/**
 * GetCampaignRankingUsersMostSubscribersUseCase
 * Loads the user (most-subscribers) leaderboard for a campaign and remaps to
 * entities, branching live-vs-ended on `campaign.active`.
 *
 * Flutter source of truth:
 * - lib/data/repository/campaigns_repository_impl.dart:13-21 —
 *   getCampaignRankingUsersMostSubscribers(campaign): if `campaign.active` →
 *   getCampaignUsersMostSubscribers(campaign.rankingFetchLimit); else →
 *   getEndedCampaignUsersMostSubscribers(campaign.id, campaign.rankingFetchLimit).
 * - lib/presentation/bottom_sheet/campaign_ranking_bottom_sheet.dart:110-118 —
 *   CampaignType.usersMostSubscribers branch calls this repository method.
 *
 * Source-shape selection (Task 3 remappers): the live `users` query yields the
 * "from user" doc shape (CampaignRankingUserRemapper.fromUserJson); the frozen
 * `ranking` subcollection yields the ranking doc shape (fromRankingJson).
 */

import { CampaignsService } from '@/domain/services/campaign/campaignsService';
import { CampaignRankingUserRemapper } from '@/domain/remappers/campaign/campaignRankingUserRemapper';
import type { CampaignEntity } from '@/domain/entities/campaign/campaignEntity';
import type { CampaignRankingUserEntity } from '@/domain/entities/campaign/campaignRankingUserEntity';

export class GetCampaignRankingUsersMostSubscribersUseCase {
  private campaignsService: CampaignsService;

  constructor() {
    this.campaignsService = CampaignsService.getInstance();
  }

  async execute(campaign: CampaignEntity): Promise<CampaignRankingUserEntity[]> {
    if (campaign.active) {
      const items = await this.campaignsService.getCampaignUsersMostSubscribers(
        campaign.id,
        campaign.rankingFetchLimit,
      );
      return CampaignRankingUserRemapper.fromUserJsonList(items).map(
        CampaignRankingUserRemapper.fromDto,
      );
    }

    const items = await this.campaignsService.getEndedCampaignUsersMostSubscribers(
      campaign.id,
      campaign.rankingFetchLimit,
    );
    return CampaignRankingUserRemapper.fromRankingJsonList(items).map(
      CampaignRankingUserRemapper.fromDto,
    );
  }
}

export const createGetCampaignRankingUsersMostSubscribersUseCase = () =>
  new GetCampaignRankingUsersMostSubscribersUseCase();
